export default function CalmGuarantee() {
  const guaranteeCards = [
    {
      icon: 'ri-hand-coin-line',
      title: 'Pay Only After Installation',
      description: 'You don\'t pay anything upfront. Payment is due only after your windows are installed and you\'re completely satisfied with the result.',
      color: 'from-[#52AB98] to-[#2B6777]'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Clear Pricing',
      description: 'Your proposal is clear and transparent from the start — no hidden fees, no surprise charges, and no pressure to upgrade along the way.',
      color: 'from-[#D9A7B0] to-[#C8959F]'
    },
    {
      icon: 'ri-ruler-line',
      title: 'Licensed PM Measures Before Ordering',
      description: 'Before anything is ordered, a licensed production manager takes final measurements to ensure every window is custom-built for an exact fit.',
      color: 'from-[#52AB98] to-[#478F82]'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Lifetime of the Home Warranty',
      description: 'Your windows are protected by a lifetime of the home warranty backed by Alside — including glass breakage so you\'re covered long after installation.',
      color: 'from-[#2B6777] to-[#1F4E5A]'
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="calm-guarantee" className="py-24 md:py-32 relative overflow-hidden bg-white">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#52AB98]/5 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#D9A7B0]/5 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6">
        {/* Trust Label Badge */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#D9A7B0]/10 to-[#52AB98]/10 rounded-full border border-[#D9A7B0]/20">
            <div className="relative w-2 h-2 bg-[#D9A7B0] rounded-full">
              <div className="absolute inset-0 bg-[#D9A7B0] rounded-full animate-ping opacity-75"></div>
            </div>
            <span className="text-sm font-semibold text-[#2B6777] uppercase tracking-wide">
              Trust & Credibility
            </span>
          </div>
        </div>

        {/* Main Headline */}
        <div className="text-center mb-8">
          <h2 className="text-5xl md:text-6xl font-heading font-bold text-[#2B6777] mb-6 tracking-tight leading-[1.1]">
            The Calm Guarantee
          </h2>
          <p className="text-2xl md:text-3xl font-semibold text-[#2B6777]/80 mb-6">
            No sales visits. <strong className="font-extrabold">NO MONEY DOWN.</strong> No surprises.
          </p>
        </div>

        {/* Story Content - Premium Card */}
        <div className="max-w-[900px] mx-auto mb-16">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 shadow-xl border border-[#C8D8E4]/30">
            <p className="text-lg text-[#2B6777]/90 leading-relaxed mb-6">
              Upgrading your windows shouldn't feel risky.
            </p>
            <p className="text-lg text-[#2B6777]/90 leading-relaxed mb-6">
              It should feel clear, fair, and fully in your control.
            </p>
            <p className="text-lg text-[#2B6777]/90 leading-relaxed mb-8">
              That's why SNAPwindows.ai is built around a simple promise:
            </p>

            {/* Emphasis Box */}
            <div className="bg-gradient-to-br from-[#52AB98]/10 to-[#D9A7B0]/10 rounded-2xl p-8 mb-8 border-l-4 border-[#52AB98]">
              <p className="text-xl md:text-2xl font-semibold text-[#2B6777] mb-4 leading-relaxed">
                <strong className="font-extrabold">NO MONEY DOWN.</strong> You pay nothing until your project is finished and you're <strong className="font-extrabold">100% SATISFIED</strong>.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-[#52AB98] text-xl"></i>
                  <span className="text-base text-[#2B6777]/80">No upfront payments.</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-[#52AB98] text-xl"></i>
                  <span className="text-base text-[#2B6777]/80">No surprise charges.</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-[#52AB98] text-xl"></i>
                  <span className="text-base text-[#2B6777]/80">No pressure to move forward.</span>
                </div>
              </div>
            </div>

            <p className="text-base text-[#2B6777]/70 italic text-center">
              This guarantee isn't a promotion. This is how we believe business should be done.
            </p>
          </div>
        </div>

        {/* Guarantee Cards - 4-Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {guaranteeCards.map((card, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl p-8 shadow-lg border border-[#C8D8E4]/20 hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden"
              style={{
                transform: 'translateY(0)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Subtle gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#52AB98]/0 to-[#D9A7B0]/0 group-hover:from-[#52AB98]/5 group-hover:to-[#D9A7B0]/5 transition-all duration-500 rounded-2xl"></div>

              <div className="relative">
                {/* Icon with gradient background */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                  <i className={`${card.icon} text-white text-3xl`}></i>
                </div>

                {/* Content */}
                <h3 className="text-xl font-heading font-semibold text-[#2B6777] mb-4 leading-tight">
                  {card.title}
                </h3>
                <p className="text-base text-[#2B6777]/70 leading-relaxed">
                  {card.description}
                </p>
              </div>

              {/* Decorative corner element */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#52AB98]/5 to-transparent rounded-bl-full"></div>
            </div>
          ))}
        </div>

        {/* Closing Statement */}
        <div className="text-center mb-12">
          <div className="max-w-[700px] mx-auto bg-gradient-to-br from-white to-[#F2F2F2] rounded-2xl p-8 shadow-lg border border-[#C8D8E4]/20">
            <p className="text-xl font-semibold text-[#2B6777] leading-relaxed">
              Your windows are installed by a factory trained, licensed and insured installation crew.
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <a
            href="https://api.leadconnectorhq.com/widget/form/0ZsIY0jYLaF8aXm2FeXI"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-10 py-5 bg-[#52AB98] text-white text-lg font-semibold rounded-xl hover:bg-[#478F82] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl hover:shadow-2xl hover:scale-105"
          >
            Get My Proposal
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
          <p className="text-sm text-[#2B6777]/50 mt-4">
            Takes ~2 minutes · No credit card · No in-home sales visit
          </p>
        </div>
      </div>
    </section>
  );
}